import { Box, Flex, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import {
  sessionCompareVoiceLabel,
  useSessionCompareDesign,
} from '#features/session-room/hooks/useSessionCompareDesign';
import {
  Button,
  DiscordIcon,
  MicOffIcon,
  MicOnIcon,
  PhoneIcon,
  PhoneOffIcon,
  type ButtonProps,
} from '#features/ui';
import { VoiceWave } from '../VoiceIndicator/VoiceWave';

export type VoiceRoomState =
  /** Голос свободен, в комнате никого. */
  | 'empty'
  /** В комнате есть люди, меня там нет. */
  | 'occupied'
  /** Я в голосе. */
  | 'joined'
  | 'connecting'
  | 'reconnecting'
  /** Режим сессии — Discord. */
  | 'discord'
  /** Голосовой чат внутри игры, без подключения через Teamplay. */
  | 'inGame'
  /** Голос выключен для сессии. */
  | 'off'
  /** Сессия ещё не началась, вход откроется позже. */
  | 'scheduled'
  /** Сессии больше нет: закончилась сама или её завершили досрочно. */
  | 'finished'
  | 'full'
  | 'kicked'
  | 'restricted'
  /** Разговор перешёл в другую вкладку этого же браузера. */
  | 'otherTab'
  /** Идёт личный звонок — микрофон занят им. */
  | 'callBusy'
  /** Связь пропала и голос отвалился сам — человек ничего не нажимал. */
  | 'connectionLost'
  | 'overloaded';

type VoiceRoomBlockProps = {
  state: VoiceRoomState;
  /** В карточке автора со свободными местами главным остаётся приглашение. */
  emphasis?: 'primary' | 'secondary';
  /** Сколько человек в голосе. Знаменателя нет: см. комментарий у `counter` ниже. */
  connectedCount?: number;
  /** Стек аватаров тех, кто в голосе (кольцо получает только говорящий). */
  avatars?: React.ReactNode;
  /** Имя того, кто говорит прямо сейчас; пусто — «никто не говорит». */
  speakingName?: string | null;
  /** Сколько я уже в голосе, «12:04». */
  duration?: string | null;
  micEnabled?: boolean;
  /**
   * Микрофон не отдала система. Отличается от выключенного намеренно: «ты замьючен» в этом
   * случае — неправда, человек ничего не выключал, и совет нажать на микрофон ему не поможет.
   */
  micDenied?: boolean;
  /**
   * Показывать ли органы управления в состоянии «я в комнате».
   *
   * Полоса над перепиской рисовала микрофон и выход ровно тогда, когда рядом уже висит
   * плавающее окно разговора с теми же двумя кнопками: два одинаковых набора в трёхстах
   * пикселях друг от друга. Управление принадлежит окну — оно переживает переходы между
   * страницами; полосе остаётся состояние.
   */
  showJoinedControls?: boolean;
  /** Когда откроется голос у ещё не начавшейся сессии. */
  opensAt?: string | null;
  /** Номер и предел попыток переподключения — «Попытка 2 из 5». */
  reconnectAttempt?: number;
  reconnectMaxAttempts?: number;
  /** До какой даты действует ограничение голоса у аккаунта. */
  restrictedUntil?: string | null;
  isBusy?: boolean;
  onJoin?: () => void;
  onLeave?: () => void;
  onToggleMic?: () => void;
  onCancel?: () => void;
  onRetry?: () => void;
  onLinkDiscord?: () => void;
  onOpenDiscord?: () => void;
  /** Напомнить, когда откроется голос у запланированной сессии. */
  onRemind?: () => void;
  /** Подписка на освободившееся место — не очередь. */
  onNotifyWhenFree?: () => void;
  /** Единственный выход у выгнанного — написать автору. */
  onMessageHost?: () => void;
  /** Раздел ограничений профиля. */
  onDetails?: () => void;
  /**
   * Где стоит блок.
   *
   * `card` — секция карточки заявки: кружок-иконка, заголовок, подробность, счётчик и кнопка во
   * всю ширину на телефоне. Отдельный экран со своим местом, и он остаётся как есть.
   *
   * `bar` — слот действия в полосе комнаты и в кластере яруса голоса: только кнопка состояния.
   * Слова там говорит сама полоса, и говорит точнее — свой счётчик блока стоял рядом с её
   * счётчиком и спорил с ним знаменателем.
   *
   * Проп меняет только оформление. Что показывать в каждом из пятнадцати состояний, по-прежнему
   * решает один и тот же `content` ниже — второй ветки рендера у состояний нет и быть не должно.
   */
  density?: 'card' | 'bar';
  /** Явная подпись входа для блока команды и голоса; остальные поверхности сохраняют значок. */
  showJoinLabel?: boolean;
};

/** Размер иконки внутри кружка ICON_BOX: один на все состояния, иначе значки прыгают. */
const ICON_IN_BOX = '17px';

const ICON_BOX = {
  w: '32px',
  h: '32px',
  flex: 'none',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
} as const;

/**
 * Мобильный размер целей нажатия — 48px, десктопный — 40px. Ниже 44px на телефоне не опускается
 * ничего: блок рассчитан на человека, который тыкает в него, не отрываясь от игры.
 */
const ACTION = {
  size: 'sm',
  h: { base: '48px', md: '40px' },
  textStyle: { base: 'sm', md: 'xs' },
  w: { base: '100%', md: 'auto' },
} as const;

/** Кнопка в паре: на мобильном делит строку, на десктопе живёт по своей ширине. */
const PAIRED_ACTION = { ...ACTION, flex: { base: 1, md: 'none' } } as const;

/**
 * То же действие в полосе и в ярусе голоса. Кнопка там не занимает строку целиком и не растёт до
 * 48px: она стоит в одном ряду с текстом полосы, и высота полосы — это её высота.
 *
 * 44px на телефоне, а не 36: в полосе комнаты эта кнопка — единственная цель голоса, и рядом с
 * ней стоит микрофон в те же 44 (§9.1). 36 пикселей были ростом под двухъярусную полосу, где
 * кнопка делила строку с заголовком и подписью; заголовка и подписи здесь больше нет, а палец
 * остался прежним. С `md` — 40, ступень десктопных целей комнаты (§5.1, §6).
 */
const BAR_ACTION = {
  ...ACTION,
  h: { base: '44px', md: '40px' },
  textStyle: 'xs',
  w: 'auto',
  flexShrink: 0,
} as const;

const BAR_PAIRED_ACTION = { ...BAR_ACTION, flex: 'none' } as const;

/**
 * Квадратная кнопка-иконка. Пока я в голосе, действий два и оба знакомы по любому войс-чату
 * (микрофон и трубка), поэтому подпись им не нужна — а без подписи ряд помещается справа от текста
 * и не занимает вторую строку карточки во всю ширину.
 */
const ICON_ACTION = {
  size: 'sm',
  h: { base: '44px', md: '40px' },
  minW: { base: '44px', md: '40px' },
  w: { base: '44px', md: '40px' },
  px: 0,
  flex: 'none',
} as const;

/**
 * Тот же квадрат в полосе. На телефоне 44 — то же правило, что у кнопки с подписью: пара
 * «микрофон + выход» здесь не встречается вовсе (пока я в комнате, слот занят настоящим
 * микрофоном комнаты), а одинокий выход при обрыве связи мимо пальца промахиваться не должен.
 * С `md` — 36: там указатель точный.
 */
const BAR_ICON_ACTION = {
  ...ICON_ACTION,
  h: { base: '44px', md: '36px' },
  minW: { base: '44px', md: '36px' },
  w: { base: '44px', md: '36px' },
} as const;

/**
 * Последняя секция карточки сессии: одна строка статуса, одна строка подробностей и не больше двух
 * кнопок. Высота ряда не меняется между состояниями — иначе карточка дёргается каждый раз, когда
 * кто-то зашёл в голос или вышел.
 *
 * Вместимость комнаты равна числу слотов сессии, поэтому «комната заполнена» и «состав собран» —
 * это одно и то же, и объяснять разницу не нужно.
 *
 * Блок видят только те, кто может войти — состав и автор. Наблюдателю вместо него достаётся
 * подпись {@link VoiceObserverNote}: кнопочный ряд с отобранной кнопкой читался как поломка.
 */
export const VoiceRoomBlock = ({
  state,
  emphasis = 'primary',
  connectedCount = 0,
  avatars,
  speakingName,
  duration,
  micEnabled = true,
  micDenied = false,
  showJoinedControls = true,
  opensAt,
  reconnectAttempt,
  reconnectMaxAttempts = 5,
  restrictedUntil,
  isBusy,
  onJoin,
  onLeave,
  onToggleMic,
  onCancel,
  onRetry,
  onLinkDiscord,
  onOpenDiscord,
  onRemind,
  onNotifyWhenFree,
  onMessageHost,
  onDetails,
  density = 'card',
  showJoinLabel = false,
}: VoiceRoomBlockProps) => {
  const { t, i18n } = useLingui();
  const proposed = useSessionCompareDesign();

  // Плотность — это оформление, а не ветка состояний: ниже общий `content`, меняются только
  // размеры кнопок и то, что помещается в одну строку на телефоне.
  const isBar = density === 'bar';
  const action: ButtonProps = isBar ? BAR_ACTION : ACTION;
  const pairedAction: ButtonProps = isBar ? BAR_PAIRED_ACTION : PAIRED_ACTION;
  const iconAction: ButtonProps = isBar ? BAR_ICON_ACTION : ICON_ACTION;

  /*
    Счётчик без знаменателя (21.09.2026).

    Было «2 of 4» на карточке и «2/4» в комнате, причём знаменатели считали РАЗНОЕ: здесь
    вместимость комнаты, там принятый состав. Один и тот же момент читался двумя числами на
    одном экране.

    Дробь убрана совсем, а не сведена к одному формату. Вместимость это технический предел,
    которым никто не думает, а состав и так виден лицами рядом. При наших числах «1/3 in voice»
    читается как счёт в матче и сообщает «двое из нас не пришли»; «1 in voice» с лицом говорит
    «там Саша» - это и втягивает. По-русски работает без форм множественного.
  */
  /*
    🔴 В свёрнутой комнате (`SessionRoomPill`, `SessionRoomBar`) с 26.09.2026 дробь есть, и
    знаменатель у неё - места сессии. Расхождение осознанное, а не забытое: карточка заявки -
    витрина, она втягивает («1 в голосе» рядом с лицом = «там Саша»), а свёрнутая комната -
    пульт идущей сессии, где вопрос «сколько нас помещается» настоящий.
  */
  const counter = String(connectedCount);

  /*
    Вход в голос: значок везде.
    =========================

    Раньше здесь было два вида одной кнопки: в полосе (окно комнаты, шторка, тред) значок, в
    карточке слово «Зайти в голос». Слово исчезло 23.09.2026 - человек видит кнопку в обоих
    местах и не должен гадать, одно это действие или два. Имя осталось там, где оно и нужно:
    в `aria-label` для экранного диктора и во всплывающей подсказке.

    Значок - трубка (15.09.2026). Был закрашенный микрофон, и после входа на этом же месте
    вставал переключатель микрофона с тем же значком: до входа кнопка читалась как «голос уже
    включён». Наушники, поставленные следом, тоже статус (в Discord это «заглушить звук».)
    Трубка - дверь, микрофон - состояние за дверью; выход у комнаты уже перечёркнутая трубка,
    возврат после обрыва - та же трубка. У автора со свободными местами залитой остаётся
    кнопка приглашения, поэтому вход в голос получает контур.
  */
  const JoinAction = () => (
    <Button
      {...iconAction}
      w={showJoinLabel || (proposed && isBar) ? 'auto' : iconAction.w}
      px={showJoinLabel ? 3 : proposed && isBar ? 2 : iconAction.px}
      variant={emphasis === 'primary' ? 'solid' : 'outline'}
      colorPalette="purple"
      aria-label={t`post:voice.room.join`}
      title={t`post:voice.room.join`}
      onClick={onJoin}
      loading={isBusy}
    >
      {showJoinLabel ? (
        <>
          <PhoneIcon />
          <span>{t`post:voice.room.join`}</span>
        </>
      ) : proposed && isBar ? (
        <>
          <PhoneIcon />
          <span>{sessionCompareVoiceLabel(i18n.locale)}</span>
        </>
      ) : (
        <PhoneIcon />
      )}
    </Button>
  );

  const content = (() => {
    switch (state) {
      case 'empty':
        return {
          inlineAction: true,
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <MicOnIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          // Общая с плавающей комнатой строка (21.09.2026): было «Voice is on» здесь и
          // «Nobody in voice» там - одно состояние двумя голосами. «Voice is on» к тому же
          // описывало настройку сессии, а не то, что в комнате пусто.
          title: t`post:voice.provider.teamplay`,
          // Подписи нет намеренно: «Discord не нужен» отвечало на вопрос, которого человек не
          // задавал, и тянуло чужой продукт в блок, который к нему отношения не имеет.
          detail: null,
          action: <JoinAction />,
        };

      case 'occupied':
        return {
          inlineAction: true,
          icon: avatars,
          title: (
            <Flex align="center" gap={2} whiteSpace="nowrap">
              <Box as="span" fontVariantNumeric="tabular-nums">
                {counter}
              </Box>
              {t`post:voice.inVoice`}
              <VoiceWave height={11} />
            </Flex>
          ),
          // Тишину не комментируем. Снаружи комнаты мы вообще не знаем, кто говорит — живое
          // состояние есть только у тех, кто внутри, — и прежняя строка «никто не говорит» стояла
          // там всегда, в том числе когда в комнате шёл разговор.
          detail: speakingName ? `${t`post:voice.room.speaking`} ${speakingName}` : null,
          action: <JoinAction />,
        };

      case 'joined':
        return {
          accent: 'status.live.solid',
          icon: avatars,
          title: micDenied ? (
            <Text color="fg.error">{t`post:voice.room.micBlocked`}</Text>
          ) : micEnabled ? (
            <Text color="status.live.fg">{t`post:voice.room.youAreIn`}</Text>
          ) : (
            t`post:voice.room.micOff`
          ),
          // Системный запрет и собственный мьют выглядели одинаково — «You're muted · you still
          // hear everyone». Человек, которому Android отказал в микрофоне, читал это как свой
          // выбор и продолжал говорить в пустоту (проверено на устройстве 27.08).
          detail: micDenied ? (
            <Text as="span" color="fg.error">
              {t`post:voice.room.micBlockedDetail`}
            </Text>
          ) : micEnabled ? (
            <Box as="span" fontVariantNumeric="tabular-nums">
              {counter}
              {duration ? ` · ${duration}` : ''}
            </Box>
          ) : (
            t`post:voice.room.micOffDetail`
          ),
          inlineAction: true,
          action: showJoinedControls ? (
            <Flex align="center" gap={2}>
              <Button
                {...iconAction}
                variant="ghost"
                aria-label={micEnabled ? t`post:voice.room.muteMic` : t`post:voice.room.unmuteMic`}
                title={micEnabled ? t`post:voice.room.muteMic` : t`post:voice.room.unmuteMic`}
                onClick={onToggleMic}
                bg={micEnabled ? 'status.live.bg' : 'bg.emphasized'}
                color={micEnabled ? 'status.live.fg' : 'fg.secondary'}
              >
                {micEnabled ? <MicOnIcon fontSize="18px" /> : <MicOffIcon fontSize="18px" />}
              </Button>
              <LeaveButton label={t`post:voice.room.leave`} onClick={onLeave} sizing={iconAction} />
            </Flex>
          ) : null,
        };

      case 'connecting':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="purple.fg">
              <Spinner />
            </Flex>
          ),
          title: t`post:voice.room.connecting`,
          detail: t`post:voice.room.connectingDetail`,
          action: (
            <Button {...action} variant="outline" color="fg.secondary" onClick={onCancel}>
              {t`post:voice.room.cancel`}
            </Button>
          ),
        };

      case 'reconnecting':
        return {
          accent: 'orange.400',
          icon: (
            <Flex {...ICON_BOX} bg="bg.warning" color="fg.warning">
              <Spinner />
            </Flex>
          ),
          title: <Text color="fg.warning">{t`post:voice.room.reconnecting`}</Text>,
          // Номер попытки важнее слова «переподключаюсь»: он показывает, что процесс конечен, и
          // после пятой попытки блок честно перейдёт в «голос не справляется».
          detail: (
            <Box as="span" fontVariantNumeric="tabular-nums">
              {reconnectAttempt
                ? `${t`post:voice.room.attempt`} ${reconnectAttempt} ${t`post:voice.of`} ${reconnectMaxAttempts} · `
                : ''}
              {t`post:voice.room.seatIsHeld`}
            </Box>
          ),
          // Выход выглядит так же, как в «я в голосе»: между этими состояниями карточка
          // переключается сама, и кнопка не должна менять форму под пальцем.
          inlineAction: true,
          action: (
            <LeaveButton label={t`post:voice.room.leave`} onClick={onLeave} sizing={iconAction} />
          ),
        };

      case 'discord':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized">
              <DiscordIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: t`post:voice.provider.discord`,
          detail: t`post:voice.room.discordDetail`,
          action: onOpenDiscord ? (
            <Button {...action} variant="outline" onClick={onOpenDiscord}>
              {t`post:voice.room.openChannel`}
            </Button>
          ) : (
            <Button {...action} variant="outline" onClick={onLinkDiscord}>
              {t`post:voice.discord.link`}
            </Button>
          ),
        };

      case 'inGame':
        return {
          icon: null,
          title: <Text color="fg.secondary">{t`post:session.room.voiceInGame`}</Text>,
          detail: null,
          action: null,
        };

      case 'off':
        return {
          dimmed: true,
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.muted">
              <MicOffIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: <Text color="fg.secondary">{t`post:voice.room.offTitle`}</Text>,
          detail: t`post:voice.room.offDetail`,
        };

      case 'finished':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <ClockGlyph />
            </Flex>
          ),
          title: <Text color="fg.secondary">{t`post:voice.room.sessionOver`}</Text>,
          detail: t`post:voice.room.sessionOverDetail`,
          // Ни кнопки входа, ни «Повторить»: возвращаться некуда, сессии больше нет.
          action: null,
        };

      case 'scheduled':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <ClockGlyph />
            </Flex>
          ),
          title: <Text color="fg.secondary">{t`post:voice.room.opensBeforeStart`}</Text>,
          detail: opensAt ? (
            <Box as="span" fontVariantNumeric="tabular-nums">
              {opensAt}
            </Box>
          ) : null,
          // Кнопки входа здесь нет вовсе — вместо неё напоминание. Выключенная кнопка звала бы
          // нажимать себя каждую минуту до открытия комнаты.
          action: onRemind ? (
            <Button {...action} variant="outline" onClick={onRemind}>
              {t`post:voice.room.remind`}
            </Button>
          ) : null,
        };

      case 'full':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <MicOnIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: t`post:voice.room.fullTitle`,
          detail: (
            <Box as="span" fontVariantNumeric="tabular-nums">
              {counter} – {t`post:voice.room.fullDetail`}
            </Box>
          ),
          // Подписка на освободившееся место, а не очередь: мест никто не бронирует.
          action: onNotifyWhenFree ? (
            <Button {...action} variant="outline" onClick={onNotifyWhenFree}>
              {t`post:voice.room.notifyWhenFree`}
            </Button>
          ) : null,
        };

      case 'kicked':
        return {
          accent: 'red.400',
          icon: (
            <Flex {...ICON_BOX} bg="bg.error" color="fg.error">
              <MicOffIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: <Text color="fg.error">{t`post:voice.room.kickedTitle`}</Text>,
          detail: t`post:voice.room.kickedDetail`,
          // Единственный выход — разговор с автором: вернуться в комнату до конца сессии нельзя.
          action: onMessageHost ? (
            <Button {...action} variant="outline" onClick={onMessageHost}>
              {t`post:voice.room.messageHost`}
            </Button>
          ) : null,
        };

      case 'restricted':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <MicOffIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: t`post:voice.room.restrictedTitle`,
          // Срок называем всегда, когда он известен: без него человек идёт в поддержку.
          detail: restrictedUntil
            ? `${t`post:voice.room.restrictedUntil`} ${restrictedUntil} · ${t`post:voice.room.restrictedReason`}`
            : t`post:voice.room.restrictedDetail`,
          action: onDetails ? (
            <Button {...action} variant="outline" onClick={onDetails}>
              {t`post:voice.room.details`}
            </Button>
          ) : null,
        };

      // Медиасервер различает участников по identity, поэтому вход в ту же комнату из соседней
      // вкладки выбивает эту. Раньше разговор здесь просто исчезал без объяснений — теперь видно,
      // что он не оборвался, а переехал, и одной кнопкой возвращается обратно.
      case 'otherTab':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <MicOffIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: t`post:voice.room.otherTabTitle`,
          detail: t`post:voice.room.otherTabDetail`,
          action: (
            <Button {...action} variant="outline" onClick={onRetry} loading={isBusy}>
              {t`post:voice.room.otherTabAction`}
            </Button>
          ),
        };

      // Микрофон один, поэтому и разговор один — то же правило, что у Discord и Telegram.
      // Кнопки здесь нет намеренно: окно звонка уже на экране, и завершать его надо там.
      case 'callBusy':
        return {
          icon: (
            <Flex {...ICON_BOX} bg="bg.emphasized" color="fg.secondary">
              <MicOffIcon fontSize={ICON_IN_BOX} />
            </Flex>
          ),
          title: t`post:voice.room.callBusyTitle`,
          detail: t`post:voice.room.callBusyDetail`,
          action: null,
        };

      case 'connectionLost':
        // Отдельно от `overloaded`: там мы обещаем, что чиним у себя, и зовём в Discord. При
        // пропавшем интернете это прямая неправда, а человеку нужно знать другое — что его
        // выбросило не по его воле и что вернуться можно одной кнопкой. Проверено на устройстве
        // 27.08: при обрыве сети голос исчезал молча, без единого слова.
        return {
          accent: 'orange.400',
          icon: (
            <Flex {...ICON_BOX} bg="bg.warning" color="fg.warning">
              <WarningGlyph />
            </Flex>
          ),
          title: <Text color="fg.warning">{t`post:voice.room.connectionLostTitle`}</Text>,
          detail: t`post:voice.room.connectionLostDetail`,
          action: (
            <Button {...pairedAction} onClick={onRetry} loading={isBusy}>
              {t`post:voice.room.retry`}
            </Button>
          ),
        };

      case 'overloaded':
      default:
        return {
          accent: 'orange.400',
          icon: (
            <Flex {...ICON_BOX} bg="bg.warning" color="fg.warning">
              <WarningGlyph />
            </Flex>
          ),
          title: <Text color="fg.warning">{t`post:voice.room.overloadedTitle`}</Text>,
          // Единственный отказ, где предлагаем запасной путь: Discord мы намеренно не выпиливали.
          detail: t`post:voice.room.overloadedDetail`,
          action: (
            <Flex align="center" gap={2.5} w={isBar ? 'auto' : { base: '100%', md: 'auto' }}>
              {onOpenDiscord ? (
                <Button {...pairedAction} variant="outline" onClick={onOpenDiscord}>
                  {t`post:voice.room.toDiscord`}
                </Button>
              ) : null}
              <Button {...pairedAction} onClick={onRetry} loading={isBusy}>
                {t`post:voice.room.retry`}
              </Button>
            </Flex>
          ),
        };
    }
  })();

  /* `in`, а не прямое обращение: у части веток ключа `action` в объекте нет вовсе. */
  const stateAction = 'action' in content ? content.action : null;

  /*
    В полосе комнаты и в ярусе голоса от блока нужно ровно одно — кнопка состояния.

    Всё, что блок умеет говорить словами, там уже сказано соседом и сказано точнее: полоса и
    ярус сами пишут «В голосе 1/2» по составу сессии, показывают лица тех, кого слышно, и имя
    говорящего. Пока блок рисовал сюда свой кружок-иконку, свой заголовок и СВОЙ счётчик
    (принятые против `teamLimit`), рядом оказывались две дроби с разными знаменателями — «1/2»
    от яруса и «1 из 3» от блока в сотне пикселей друг от друга, — а на телефоне слот разрастался
    со ста сорока двух пикселей вместо семидесяти двух и выдавливал превью сообщения до
    семнадцати знаков. §14 брифа запрещает и то и другое.

    Кнопка при этом остаётся общей и единственной: все пятнадцать состояний входа — войти,
    вернуться, отменить, «занято другой вкладкой» — по-прежнему решает один и тот же `content`
    выше. Второй ветки состояний здесь не появилось, отсюда уходит только оформление.

    Состояния без кнопки («голос выключен», «идёт личный звонок») отдают пустой ряд, а не
    заглушку: слово о них у полосы и яруса своё, а дохлая кнопка хуже её отсутствия.
  */
  if (isBar) {
    return (
      <Flex
        data-testid="voice-room-block"
        data-voice-state={state}
        data-density={density}
        align="center"
        flexShrink={0}
      >
        {stateAction}
      </Flex>
    );
  }

  return (
    <Flex
      data-testid="voice-room-block"
      data-voice-state={state}
      data-density={density}
      align={content.inlineAction ? 'center' : { base: 'flex-start', md: 'center' }}
      direction={content.inlineAction ? 'row' : { base: 'column', md: 'row' }}
      gap={3}
      // Без горизонтального отступа: блок вставляется в колонку карточки сессии, у которой свой
      // px={4}, и собственный отступ уводил иконку вправо относительно аватаров состава выше.
      px={0}
      /*
        Линия и отступ сверху переехали на ряд разговора (23.09.2026).

        Здесь они означали «голос это отдельная полоса карточки», и рядом с ним кнопка переписки
        вставала криво: у неё ни этой линии, ни выравнивания по ней, и ряд читался как две
        случайно сошедшиеся вещи. Но полоса это ВЕСЬ разговор - голос и переписка вместе, - и
        отделять от состава выше надо её целиком.
      */
      py={0}
      opacity={content.dimmed ? 0.72 : 1}
      boxShadow={
        content.accent
          ? `inset 2px 0 0 var(--chakra-colors-${String(content.accent).replace('.', '-')})`
          : undefined
      }
    >
      <Flex
        align="center"
        gap={3}
        flex={1}
        minW={0}
        w={content.inlineAction ? 'auto' : { base: '100%', md: 'auto' }}
      >
        {content.icon}
        {/* Смена состояния — кросс-фейд текста, не перестройка ряда: карточка не должна дёргаться
            каждый раз, когда кто-то зашёл в голос или вышел. */}
        <Box
          key={state}
          minW={0}
          animation="fadeIn var(--chakra-durations-fast) var(--chakra-easings-ease-in-smooth)"
          css={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
        >
          {state !== 'empty' && state !== 'discord' && state !== 'off' ? (
            <Text
              textStyle="caption"
              color="fg.muted"
              mb={0.5}
            >{t`post:voice.provider.teamplay`}</Text>
          ) : null}
          {/* Ступени шкалы, а не размер с весом врозь: sm/600 это title, xs/600 это label. */}
          <Box textStyle={{ base: 'title', md: 'label' }}>{content.title}</Box>
          {content.detail ? (
            <Box textStyle="caption" color="fg.secondary" mt={0.5} lineHeight="moderate">
              {content.detail}
            </Box>
          ) : null}
        </Box>
      </Flex>

      {/* На мобильном действие уезжает под текст и растягивается на всю ширину: попасть пальцем
          по кнопке в правом углу узкой карточки неудобно. */}
      {stateAction ? (
        <Box
          ml={content.inlineAction ? 'auto' : { base: 0, md: 'auto' }}
          w={content.inlineAction ? 'auto' : { base: '100%', md: 'auto' }}
          flex="none"
        >
          {stateAction}
        </Box>
      ) : null}
    </Flex>
  );
};

/**
 * Выход из голоса. Иконка вместо подписи: это не главное действие карточки, а подпись во всю
 * ширину делала его самым заметным элементом блока. Красным подсвечивается только на наведении —
 * постоянный красный читался бы как ошибка, а не как выход.
 */
const LeaveButton = ({
  label,
  onClick,
  // Размеры приходят снаружи: в полосе кнопка меньше, чем в карточке, и решает это один проп
  // плотности блока — не сам выход.
  sizing = ICON_ACTION,
}: {
  label: string;
  onClick?: () => void;
  sizing?: ButtonProps;
}) => (
  <Button
    {...sizing}
    variant="outline"
    aria-label={label}
    title={label}
    onClick={onClick}
    color="fg.secondary"
    _hover={{ color: 'fg.error', borderColor: 'fg.error' }}
  >
    <PhoneOffIcon fontSize={ICON_IN_BOX} />
  </Button>
);

const Spinner = () => (
  <Box
    as="span"
    display="inline-flex"
    animation="spin"
    css={{ '@media (prefers-reduced-motion: reduce)': { animation: 'none' } }}
  >
    <svg
      viewBox="0 0 24 24"
      width="17"
      height="17"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.2-8.6" />
    </svg>
  </Box>
);

const ClockGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);

const WarningGlyph = () => (
  <svg
    viewBox="0 0 24 24"
    width="17"
    height="17"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21.7 18l-8-14a2 2 0 0 0-3.5 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.7-3" />
    <path d="M12 9v4" />
    <path d="M12 17h.01" />
  </svg>
);
